import {PayloadAction, createSlice} from '@reduxjs/toolkit';
import type {ForecastVariableKey} from '../../utils/WeatherMap/forecastCatalog';

/** A layer the weather map draws in observations mode. */
export type ObservationLayer = 'stations' | 'rainRadar' | 'lightning';

/**
 * What the weather map shows, remembered on this device only -- never synced
 * to the server (FE #327, Q2). The webapp persists the slice; mobile does not
 * register it yet and keeps its own `prefs.weathermap`, which is why it sits
 * outside `CommonRootState` and the selectors read it defensively.
 *
 * There is deliberately no `mode` (observations or forecast): `/map` without a
 * `mode` in the URL always opens on observations (Q1, Q4), and anything held
 * here is written to storage.
 */
export type MapPreferencesState = {
  observations: Record<ObservationLayer, boolean>;
  forecast: {
    /**
     * The last forecast map the user picked: the unified map's forecast mode
     * opens on it when the URL names none (#327 MR5c). Can point at the
     * user's own builder config, so LOGOUT clears it.
     */
    configId: number | null;
    /**
     * The wind-particle animation, on runs that carry wind: one device-wide
     * preference (not per config), kept across a sign-out. Read it through
     * `selectForecastWindParticles`, which defaults a blob stored before
     * the key existed to on.
     */
    windParticles: boolean;
    /**
     * The rail variable last chosen (#327 MR5d): what forecast mode's Auto
     * draws when the URL names none, kept across a sign-out. Null until one
     * is chosen; read it through `selectForecastVariable`, which defaults to
     * temperature.
     */
    variable: ForecastVariableKey | null;
  };
};

const initialState: MapPreferencesState = {
  observations: {stations: true, rainRadar: true, lightning: true},
  forecast: {configId: null, windParticles: true, variable: null},
};

const mapPreferencesSlice = createSlice({
  name: 'mapPreferences',
  initialState,
  reducers: {
    setObservationLayer: (
      state,
      {payload}: PayloadAction<{layer: ObservationLayer; enabled: boolean}>,
    ) => {
      // Spread rather than assign through: autoMergeLevel2 restores
      // `observations` exactly as stored, so it can lack a layer added later.
      state.observations = {
        ...state.observations,
        [payload.layer]: payload.enabled,
      };
    },
    /** Remember the forecast map the user picked; null forgets it. */
    setSavedForecastConfig: (
      state,
      {payload}: PayloadAction<number | null>,
    ) => {
      // Spread: a rehydrated `forecast` may predate a key (autoMergeLevel2).
      state.forecast = {...state.forecast, configId: payload};
    },
    setForecastWindParticles: (state, {payload}: PayloadAction<boolean>) => {
      state.forecast = {...state.forecast, windParticles: payload};
    },
    /** Remember the rail variable the user chose. */
    setForecastVariable: (
      state,
      {payload}: PayloadAction<ForecastVariableKey>,
    ) => {
      state.forecast = {...state.forecast, variable: payload};
    },
  },
  extraReducers: (builder) => {
    // The layer toggles, the wind animation and the variable are device
    // preferences and survive a sign-out; the saved forecast map may be the user's own config
    // and does not. Handled
    // here rather than in an app's root reducer so the rule travels with the
    // slice when mobile registers it. The app must keep this slice through
    // its LOGOUT reset for the case to see the old state.
    builder.addMatcher(
      (action) => action.type === 'LOGOUT',
      (state) => {
        state.forecast = {...state.forecast, configId: null};
      },
    );
  },
});

export const {
  setObservationLayer,
  setSavedForecastConfig,
  setForecastWindParticles,
  setForecastVariable,
} = mapPreferencesSlice.actions;

export default mapPreferencesSlice.reducer;
